<script>
import LocalStorageService from "@/services/LocalStorageService";
import LogoutIcon from "@/icons/LogoutIcon.vue";
const localStorageService = LocalStorageService.getService();

export default {
  name: "SidebarUserCard",
  components: { LogoutIcon },
  data() {
    return {
      showUserCard: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    userName() {
      return `${this.user.first_name} ${this.user.last_name}`;
    },
    userRole() {
      return this.user.scopes.join(", ");
    },
    totalMix() {
      return this.user?.file_time?.total_mix || 0;
    },
    totalMixHours() {
      return this.totalMix / 3600;
    },
    monthlyLimitHours() {
      return this.user?.time_limit || 0;
    },
    remainingTimeHours() {
      return Math.max(this.monthlyLimitHours - this.totalMixHours, 0);
    },
  },
  methods: {
    timeInHours(time) {
      return `${time.toFixed(2)} часов`;
    },
    logout() {
      localStorageService.clearToken();
      window.location.reload();
    },
  },
};
</script>

<template>
  <div v-if="user">
    <el-popover
      v-model="showUserCard"
      trigger="click"
      popper-class="uc-popover"
      placement="bottom-end"
      :width="264"
    >
      <div class="uc-menu">
        <!-- Identity header -->
        <div class="uc-menu__head">
          <span class="uc-menu__avatar">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="3.4" stroke="#fff" stroke-width="1.6" />
              <path
                d="M5.5 19.5a6.5 6.5 0 0 1 13 0"
                stroke="#fff"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </span>
          <span class="uc-menu__id">
            <span class="uc-menu__name">{{ userName }}</span>
            <span class="uc-menu__role">{{ userRole }}</span>
          </span>
        </div>

        <!-- Stats -->
        <div class="uc-stats">
          <div class="uc-stat">
            <span class="uc-stat__label">Размечено в этом месяце</span>
            <span class="uc-stat__value">{{ timeInHours(totalMixHours) }}</span>
          </div>
          <template v-if="monthlyLimitHours > 0">
            <div class="uc-stat">
              <span class="uc-stat__label">Лимит</span>
              <span class="uc-stat__value">{{
                timeInHours(monthlyLimitHours)
              }}</span>
            </div>
            <div class="uc-stat">
              <span class="uc-stat__label">Осталось</span>
              <span
                class="uc-stat__value"
                :class="{ 'uc-stat__value--danger': remainingTimeHours === 0 }"
                >{{ timeInHours(remainingTimeHours) }}</span
              >
            </div>
          </template>
        </div>

        <!-- Logout -->
        <button class="uc-logout" @click="logout">
          <LogoutIcon style="height: 16px; width: 16px" />
          <span>Выход</span>
        </button>
      </div>

      <template #reference>
        <div class="user-chip">
          <span class="user-chip__avatar">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle cx="12" cy="8" r="3.4" stroke="#fff" stroke-width="1.6" />
              <path
                d="M5.5 19.5a6.5 6.5 0 0 1 13 0"
                stroke="#fff"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </span>
          <span class="user-chip__text">
            <span class="user-chip__name">{{ userName }}</span>
            <span class="user-chip__role">{{ userRole }}</span>
          </span>
          <i class="user-chip__caret el-icon-arrow-down"></i>
        </div>
      </template>
    </el-popover>
  </div>
</template>

<style scoped lang="scss">
.user-chip {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 38px;
  padding: 0 10px 0 6px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--accent);
    border-color: var(--border-strong);
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    flex-shrink: 0;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--foreground);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }

  &__role {
    font-size: 10.5px;
    color: var(--muted-foreground);
    white-space: nowrap;
  }

  &__caret {
    color: var(--muted-foreground);
    font-size: 12px;
    margin-left: 2px;
    flex-shrink: 0;
  }
}
</style>

<!-- Popover content is teleported by Element Plus → must be global -->
<style lang="scss">
.el-popover.el-popper.uc-popover {
  padding: 0;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);

  // no pointer arrow
  .el-popper__arrow {
    display: none !important;
  }
}

.uc-menu {
  display: flex;
  flex-direction: column;
  padding: 8px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px 12px;
  }
  &__avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    svg {
      width: 18px;
      height: 18px;
    }
  }
  &__id {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
  }
  &__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--foreground);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__role {
    font-size: 11px;
    color: var(--muted-foreground);
  }
}

.uc-stats {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  border-radius: 10px;
  background: var(--muted);
  border: 1px solid var(--border);
}

.uc-stat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 4px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }

  &__label {
    font-size: 12px;
    color: var(--muted-foreground);
  }
  &__value {
    font-size: 13px;
    font-weight: 600;
    color: var(--foreground);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    &--danger {
      color: #ef4444;
    }
  }
}

.uc-logout {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: transparent;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  svg {
    color: currentColor;
  }
  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.35);
  }
}
</style>
